import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import { Period, PresetKey, presetPeriod, presets } from '../../models/time-range';
import {
  endOfMinute,
  formatDuration,
  formatPeriod,
  monthCells,
  parseLocal,
  toDateInput,
  toTimeInput,
  validatePeriod,
} from '../../utils/period';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons/Icons';
import TimeField from './TimeField';
import './PeriodPicker.scss';

interface PeriodPickerProps {
  value: Period;
  onChange: (period: Period) => void;
}

/** What the dialog edits before it is applied: the inputs' raw values and the month the calendar shows. */
interface Draft {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
  preset: PresetKey | 'custom';
  /** Whether the next calendar click picks the start day or the end day. */
  stage: 'start' | 'end';
  view: { year: number; month: number };
}

const pad = (n: number) => String(n).padStart(2, '0');

const draftOf = (preset: Draft['preset'], from: Date, to: Date): Draft => ({
  fromDate: toDateInput(from),
  fromTime: toTimeInput(from),
  toDate: toDateInput(to),
  toTime: toTimeInput(to),
  preset,
  stage: 'start',
  view: { year: to.getFullYear(), month: to.getMonth() },
});

/** The period button and its dialog: presets, a range calendar and from/to day and time inputs. */
const PeriodPicker = ({ value, onChange }: PeriodPickerProps) => {
  const { t } = useTranslation('common');
  const [draft, setDraft] = useState<Draft>();
  const root = useRef<HTMLDivElement>(null);
  const id = useId();
  const open = draft !== undefined;

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setDraft(undefined);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDraft(undefined);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const custom = value.preset === 'custom';
  const trigger = (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      className={`PeriodPicker__trigger${custom || open ? ' PeriodPicker__trigger--active' : ''}`}
      onClick={() => setDraft(open ? undefined : draftOf(value.preset, value.from, value.to))}
    >
      <CalendarIcon />
      <span className="PeriodPicker__trigger-text">
        <span className="PeriodPicker__trigger-kind">{t(custom ? 'period.custom' : 'period.label')}</span>
        <span className="PeriodPicker__trigger-value">{formatPeriod(value.from, value.to)}</span>
      </span>
      <ChevronDownIcon />
    </button>
  );

  if (!draft) {
    return (
      <div ref={root} className="PeriodPicker">
        {trigger}
      </div>
    );
  }

  const now = new Date();
  const from = parseLocal(draft.fromDate, draft.fromTime);
  const to = parseLocal(draft.toDate, draft.toTime);
  const end = to && endOfMinute(to);
  const error = validatePeriod(from, end, now);
  const todayIso = toDateInput(now);
  const { year, month } = draft.view;
  const atLastMonth = year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth());
  const monthLabel = `${t(`period.months.${month}`)} ${year}`;

  const edit = (patch: Partial<Draft>) => setDraft({ ...draft, preset: 'custom', ...patch });
  const pickPreset = (key: PresetKey) => {
    const p = presetPeriod(key, now);
    setDraft(draftOf(key, p.from, p.to));
  };
  const pickDay = (iso: string) => {
    if (draft.stage === 'start') edit({ fromDate: iso, fromTime: '00:00', toDate: iso, toTime: '23:59', stage: 'end' });
    else if (iso < draft.fromDate) edit({ fromDate: iso, toDate: draft.fromDate, stage: 'start' });
    else edit({ toDate: iso, stage: 'start' });
  };
  const onDate = (key: 'fromDate' | 'toDate') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(v);
    edit({ [key]: v, ...(m ? { view: { year: +m[1], month: +m[2] - 1 } } : {}) });
  };
  const onTime = (key: 'fromTime' | 'toTime') => (v: string) => edit({ [key]: v });
  const shiftMonth = (by: number) => {
    const d = new Date(year, month + by, 1);
    setDraft({ ...draft, view: { year: d.getFullYear(), month: d.getMonth() } });
  };
  const apply = () => {
    if (error || !from || !end) return;
    // A preset left untouched is taken again now, so "last 24 hours" ends at the moment it is applied.
    onChange(draft.preset === 'custom' ? { preset: 'custom', from, to: end } : presetPeriod(draft.preset));
    setDraft(undefined);
  };

  return (
    <div ref={root} className="PeriodPicker">
      {trigger}
      <div role="dialog" aria-label={t('period.dialog')} className="PeriodPicker__dialog">
        <div className="PeriodPicker__presets">
          <p>{t('period.quick')}</p>
          {presets.map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={draft.preset === key}
              className={`PeriodPicker__preset${draft.preset === key ? ' PeriodPicker__preset--active' : ''}`}
              onClick={() => pickPreset(key)}
            >
              {t(`period.presets.${key}`)}
            </button>
          ))}
        </div>

        <div className="PeriodPicker__main">
          <div className="PeriodPicker__month">
            <button type="button" aria-label={t('period.prevMonth')} onClick={() => shiftMonth(-1)}>
              <ChevronLeftIcon size={18} />
            </button>
            <span aria-live="polite">{monthLabel}</span>
            <button
              type="button"
              aria-label={t('period.nextMonth')}
              disabled={atLastMonth}
              onClick={() => shiftMonth(1)}
            >
              <ChevronRightIcon size={18} />
            </button>
          </div>

          <div className="PeriodPicker__calendar">
            <div aria-hidden="true" className="PeriodPicker__dows">
              {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                <span key={d}>{t(`period.dows.${d}`)}</span>
              ))}
            </div>
            <div role="group" aria-label={monthLabel} className="PeriodPicker__days">
              {monthCells(year, month).map((day, i) => {
                if (day === null) return <span key={i} />;
                const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
                const isStart = iso === draft.fromDate;
                const isEnd = iso === draft.toDate;
                const inside = iso > draft.fromDate && iso < draft.toDate;
                const future = iso > todayIso;
                const today = iso === todayIso;
                const cls = [
                  'PeriodPicker__day',
                  isStart || isEnd ? 'PeriodPicker__day--edge' : '',
                  isStart && !isEnd ? 'PeriodPicker__day--start' : '',
                  isEnd && !isStart ? 'PeriodPicker__day--end' : '',
                  inside ? 'PeriodPicker__day--inside' : '',
                  today ? 'PeriodPicker__day--today' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const label = [
                  `${day} ${t(`period.months.${month}`)} ${year}`,
                  isStart ? t('period.dayStart') : '',
                  isEnd ? t('period.dayEnd') : '',
                  today ? t('period.dayToday') : '',
                ]
                  .filter(Boolean)
                  .join(', ');
                return (
                  <button
                    key={i}
                    type="button"
                    className={cls}
                    disabled={future}
                    aria-pressed={isStart || isEnd}
                    aria-label={label}
                    onClick={() => pickDay(iso)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <p className="PeriodPicker__hint">{t(draft.stage === 'end' ? 'period.hintEnd' : 'period.hintStart')}</p>
          </div>

          <div className="PeriodPicker__fields">
            {(
              [
                ['from', 'fromDate', 'fromTime', false],
                ['to', 'toDate', 'toTime', error === 'order'],
              ] as const
            ).map(([legend, dateKey, timeKey, bad]) => (
              <fieldset key={legend}>
                <legend>{t(`period.${legend}`)}</legend>
                <div>
                  <label>
                    {t('period.day')}
                    <input
                      type="date"
                      value={draft[dateKey]}
                      max={todayIso}
                      aria-invalid={bad}
                      onChange={onDate(dateKey)}
                    />
                  </label>
                  <div className="PeriodPicker__time">
                    <span id={`${id}-${legend}-time`}>{t('period.time')}</span>
                    <TimeField
                      value={draft[timeKey]}
                      labelledBy={`${id}-${legend}-time`}
                      invalid={bad}
                      onChange={onTime(timeKey)}
                    />
                  </div>
                </div>
              </fieldset>
            ))}
          </div>
        </div>

        <div className="PeriodPicker__footer">
          <div className="PeriodPicker__summary">
            {error ? (
              <span role="alert" className="PeriodPicker__error">
                {t(`period.errors.${error}`)}
              </span>
            ) : (
              from && to && <strong>{formatPeriod(from, to)}</strong>
            )}
            <span>
              {t('period.duration', {
                duration: !error && from && end ? formatDuration(end.getTime() - from.getTime()) : '—',
              })}
            </span>
          </div>
          <div className="PeriodPicker__actions">
            <button type="button" className="PeriodPicker__cancel" onClick={() => setDraft(undefined)}>
              {t('period.cancel')}
            </button>
            <button type="button" className="PeriodPicker__apply" disabled={error !== undefined} onClick={apply}>
              {t('period.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeriodPicker;
